import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { COLORS, FONTS } from './theme';
import GoldDivider from './GoldDivider';

const LINE_1 = 'No screen. No scroll.';
const LINE_2 = 'A mind that’s actually switched on.';
const WORDS_1 = LINE_1.split(' ');
const WORDS_2 = LINE_2.split(' ');
const TOTAL = WORDS_1.length + WORDS_2.length;

// Scroll-linked ink fill: the statement starts faint and darkens
// word-by-word as it travels up the viewport.
const BenefitStrip: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const statementRef = useRef<HTMLParagraphElement>(null);
  const [litCount, setLitCount] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      setLitCount(TOTAL);
      return;
    }
    let ticking = false;
    const render = () => {
      ticking = false;
      const el = statementRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.min(1, Math.max(0, (vh * 0.88 - r.top) / (vh * 0.52)));
      setLitCount(Math.round(p * TOTAL));
    };
    const queue = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(render);
      }
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    queue();
    return () => {
      window.removeEventListener('scroll', queue);
      window.removeEventListener('resize', queue);
    };
  }, [reduceMotion]);

  const word = (w: string, i: number) => (
    <span
      key={i}
      className="inline-block transition-opacity duration-500"
      style={{ opacity: i < litCount ? 1 : 0.15 }}
    >
      {w}
    </span>
  );

  return (
    <section className="py-[120px] text-center">
      <div className="w-full max-w-[640px] md:max-w-[760px] mx-auto px-[26px] md:px-10">
        <p
          ref={statementRef}
          className="font-bold leading-[1.18] mb-3 [text-wrap:balance]"
          style={{
            fontFamily: FONTS.heading,
            color: COLORS.nearBlack,
            fontSize: 'clamp(34px, 9.5vw, 56px)',
          }}
        >
          {WORDS_1.map((w, i) => (
            <React.Fragment key={i}>
              {word(w, i)}
              {i < WORDS_1.length - 1 && ' '}
            </React.Fragment>
          ))}
          <br />
          <span className="italic" style={{ color: COLORS.redInk }}>
            {WORDS_2.map((w, i) => (
              <React.Fragment key={i}>
                {word(w, WORDS_1.length + i)}
                {i < WORDS_2.length - 1 && ' '}
              </React.Fragment>
            ))}
          </span>
        </p>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px -8% 0px' }}
          transition={{ duration: 1, delay: 0.15, ease: [0.22, 0.61, 0.36, 1] }}
          className="my-[38px]"
        >
          <GoldDivider />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px -8% 0px' }}
          transition={{ duration: 1, delay: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
          className="italic text-xl"
          style={{ fontFamily: FONTS.body, color: COLORS.brown }}
        >
          The first AI worthy of your family&rsquo;s time.
        </motion.p>
      </div>
    </section>
  );
};

export default BenefitStrip;
